import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import {
  Subtitle1,
  makeStyles,
  Button,
  Label,
  Text,
  Input,
  Divider,
  OverlayDrawer,
  DrawerHeader,
  DrawerHeaderTitle,
  DrawerBody
} from "@fluentui/react-components";
import { ArrowClockwise24Regular, ArrowLeftRegular, CopyRegular } from "@fluentui/react-icons";
import styles from "./EnterOnDeviceModal.scss";
import useBreakpoints from "../hooks/useBreakpoints";
import notificationsCenter from "../../singleton/NotificationsCenter";

const useStyles = makeStyles({
  button: {
    width: "100%",
    marginTop: "12px"
  },
  dialogContent: {
    gap: "8px"
  },
  accessContent: {
    display: "flex",
    flexDirection: "column",
    padding: "5px var(--Horizontal-None, 0px) var(--Font-size-100, 10px) var(--Horizontal-None, 0px)",
    gap: "15px",
    paddingBottom: "16px"
  },
  accessContentBottom: {
    display: "flex",
    flexDirection: "column",
    padding: "var(--Font-size-100, 10px) var(--Horizontal-None, 0px) 5px var(--Horizontal-None, 0px)",
    gap: "15px",
    paddingTop: "16px"
  },
  titleContainer: {
    display: "flex",
    gap: "8px"
  },
  labelAccessVR: {
    color: "var(--White, #FFF)",
    fontSize: "18px",
    fontStyle: "normal",
    fontWeight: "600",
    lineHeight: "normal"
  },
  vrContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "20px"
  },
  vrContainerInner: {
    display: "flex",
    flexDirection: "column",
    gap: "6px"
  },
  inputVr: {
    width: "100%",
    backgroundColor: "rgba(245, 245, 245, 0.1)",
    borderBottom: "none",
    "& input": {
      color: "#fff",
      cursor: "default"
    },
    "&:after": {
      borderBottom: "none"
    }
  },
  textCode: {
    color: " var(--Gray-5, #E0E0E0)",
    fontSize: " 30px",
    fontStyle: " normal",
    fontWeight: " 700",
    lineHeight: " normal",
    letterSpacing: " 8px"
  },
  divider: {
    paddingTop: "5px",
    paddingBottom: "4px"
  },
});

export function EnterOnDevice({
  shortUrl,
  code,
  loadingCode,
  headsetConnected,
  unsupportedBrowser,
  onEnterOnConnectedHeadset,
  attemptLink
}) {
  const classes = useStyles();

  const copyCode = () => {
    notificationsCenter.success("VR Code copied to clipboard");
    navigator.clipboard
      .writeText(shortUrl)
      .then(() => {})
      .catch(err => {
        console.error("Failed to copy: ", err);
      });
  };

  return (
    <>
      <div className={classes.accessContent}>
        <Label className={classes.labelAccessVR}>
          <FormattedMessage
            id="enter-on-device-modal.heading"
            defaultMessage="Access in your Wireless Headset or Phone"
          />
        </Label>
        <div className={classes.vrContainer}>
          <div className={classes.vrContainerInner}>
            <Text>
              <FormattedMessage
                id="enter-on-device-modal.short-url-directions"
                defaultMessage="Firstly, go to the following address from your device browser:"
              />
            </Text>
            <Input
              readOnly
              value={shortUrl}
              contentAfter={<CopyRegular style={{ cursor: "pointer" }} onClick={copyCode} />}
              appearance="filled-lighter"
              className={classes.inputVr}
            />
          </div>
          <div className={classes.vrContainerInner}>
            <Text>
              <FormattedMessage
                id="enter-on-device-modal.code-directions"
                defaultMessage="Secondly, enter this one-time code we provide for you:"
              />
            </Text>
            {code && <Text className={classes.textCode}>{code}</Text>}
            {!code && !loadingCode && (
              <div className={classes.vrContainerInner}>
                <Text size="600">
                  <FormattedMessage
                    id="enter-on-device-modal.code-expired-message"
                    defaultMessage="The code has expired. Click below to generate a new one."
                  />
                </Text>
                <Button appearance="primary" icon={<ArrowClockwise24Regular />} onClick={attemptLink}>
                  <FormattedMessage id="enter-on-device-modal.generate-new-code" defaultMessage="Generate New Code" />
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
      {headsetConnected && !unsupportedBrowser && (
        <>
          <Divider className={classes.divider} />
          <div className={classes.accessContentBottom}>
            <Label className={classes.labelAccessVR}>
              <FormattedMessage
                id="enter-on-device-modal.headset-connected-heading"
                defaultMessage="Access through a connected headset"
              />
            </Label>
            <Text>
              <FormattedMessage
                id="enter-on-device-modal.headset-connected-message"
                defaultMessage="Select the button if your VR headset is connected to this device."
              />
            </Text>
          </div>
          <Button className={classes.button} appearance="primary" onClick={onEnterOnConnectedHeadset}>
            <FormattedMessage id="enter-on-device-modal.enter-in-vr-button" defaultMessage="Enter in VR" />
          </Button>
        </>
      )}
    </>
  );
}

export function EnterOnDeviceModal({
  shortUrl,
  loadingCode,
  code,
  headsetConnected,
  unsupportedBrowser,
  onEnterOnConnectedHeadset,
  onBack,
  attemptLink
}) {
  const classes = useStyles();
  const breakpoint = useBreakpoints();
  const [open, setIsOpen] = useState(true);

  const callbackOpen = async cb => {
    setIsOpen(false);
    setTimeout(() => {
      cb();
    }, 100);
  };

  return (
    <OverlayDrawer
      position={["xs", "sm", "md"].includes(breakpoint) ? "bottom" : "end"}
      open={open}
      modalType="non-modal"
      className={styles.entryCard}
    >
      <DrawerHeader>
        <DrawerHeaderTitle style={{ width: "100%" }}>
          <Button
            onClick={() => callbackOpen(onBack)}
            style={{ height: "30px", width: "30px", marginRight: "10px" }}
            icon={<ArrowLeftRegular style={{ width: "18px" }} />}
          ></Button>
          <Subtitle1>
            <FormattedMessage id="enter-on-device-modal.title" defaultMessage="Enter on VR" />
          </Subtitle1>
        </DrawerHeaderTitle>
      </DrawerHeader>
      <DrawerBody className={classes.dialogContent}>
        <EnterOnDevice
          shortUrl={shortUrl}
          loadingCode={loadingCode}
          code={code}
          headsetConnected={headsetConnected}
          unsupportedBrowser={unsupportedBrowser}
          onEnterOnConnectedHeadset={() => callbackOpen(onEnterOnConnectedHeadset)}
          attemptLink={attemptLink}
        />
      </DrawerBody>
    </OverlayDrawer>
  );
}

EnterOnDeviceModal.propTypes = {
  shortUrl: PropTypes.string.isRequired,
  loadingCode: PropTypes.bool,
  code: PropTypes.string,
  headsetConnected: PropTypes.bool,
  unsupportedBrowser: PropTypes.bool,
  onEnterOnConnectedHeadset: PropTypes.func,
  onBack: PropTypes.func,
  attemptLink: PropTypes.func
};

EnterOnDevice.propTypes = {
  shortUrl: PropTypes.string.isRequired,
  loadingCode: PropTypes.bool,
  code: PropTypes.string,
  headsetConnected: PropTypes.bool,
  unsupportedBrowser: PropTypes.bool,
  onEnterOnConnectedHeadset: PropTypes.func,
  attemptLink: PropTypes.func
};
